import { atom, useAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigateWithSlug } from "routes";
import { v4 as uuid } from "uuid";

import { ModelType } from "@/pages/ModelTool/ModelEditorStore";
import { createModelToolStorageKey } from "@/pages/ModelTool/utils/createLocalStorageKey";
import { useCurrentAccount } from "@/providers/account";
import { NavigateOptions } from "@tanstack/react-location";

export type Tab = {
  id: string;
  label?: string;
  modelId?: string;
  dashboardId?: string;
  tileId?: string;
  latestPreviewId?: string;
  type: ModelType | "empty" | "dashboard";
};

const tabsAtom = atomFamily((accountId: string) =>
  atom<Tab[]>(getSavedTabs(accountId)),
);

const getSavedTabs = (accountId: string) => {
  try {
    const savedTabs = localStorage.getItem(
      createModelToolStorageKey("openTabs", accountId),
    );
    if (savedTabs) {
      return JSON.parse(savedTabs) as Tab[];
    }
    return [];
  } catch (error) {
    return [];
  }
};

const currentTabSelectedAtom = atomFamily((accountId: string) =>
  atom<string | null>(getSavedCurrentTab(accountId)),
);

const getSavedCurrentTab = (accountId: string) => {
  try {
    return (
      localStorage.getItem(
        createModelToolStorageKey("currentTab", accountId),
      ) || null
    );
  } catch (error) {
    return null;
  }
};

export const useModelTabs = () => {
  const { id } = useCurrentAccount();
  return useAtom(tabsAtom(id));
};

export const useAutoSaveTabs = () => {
  const { id } = useCurrentAccount();
  const [tabs] = useModelTabs();
  useEffect(() => {
    try {
      localStorage.setItem(
        createModelToolStorageKey("openTabs", id),
        JSON.stringify(tabs),
      );
    } catch (error) {}
  }, [id, tabs]);
};

export const useCurrentTabAtom = () => {
  const { id } = useCurrentAccount();
  const [curTab, setCurTab] = useAtom(currentTabSelectedAtom(id));
  const [tabs] = useModelTabs();

  const currentTab = useMemo(
    () => tabs.find((t) => t.id === curTab),
    [tabs, curTab],
  );

  const setCurrentTab = useCallback(
    (tabId: string | null) => {
      setCurTab(tabId);
      try {
        if (tabId)
          localStorage.setItem(
            createModelToolStorageKey("currentTab", id),
            tabId,
          );
        else
          localStorage.removeItem(createModelToolStorageKey("currentTab", id));
      } catch (error) {}
    },
    [id, setCurTab],
  );

  return { currentTab, setCurrentTab };
};

const useCurrentTab = () => useCurrentTabAtom().currentTab;

const useSetCurrentTab = () => {
  const { currentTab, setCurrentTab } = useCurrentTabAtom();
  const [tabs, setTabs] = useModelTabs();
  const navigate = useNavigateWithSlug();
  return useCallback(
    (
      tab: Omit<Tab, "id">,
      navigateOptions: Pick<
        NavigateOptions<{}>,
        "search" | "replace" | "hash"
      > = { search: true },
    ) => {
      const existingTab = tabs.find((t) => {
        if (tab.type === "model" || tab.type === "draft") {
          return t.modelId === tab.modelId;
        }
        if (tab.type === "dashboard") {
          return t.dashboardId === tab.dashboardId;
        }
        return false;
      });

      if (existingTab) {
        setCurrentTab(existingTab.id);
        navigate({ ...getTabLink(existingTab), ...navigateOptions });
        return;
      }

      if (currentTab?.type === "empty") {
        const tabIndex = tabs.findIndex((t) => t.id === currentTab.id);
        if (tabIndex !== -1) {
          const updatedTab = { ...tabs[tabIndex], ...tab };
          const newTabs = tabs.map((t) =>
            t.id === currentTab.id ? updatedTab : t,
          );
          setTabs(newTabs);
          navigate({ ...getTabLink(updatedTab), ...navigateOptions });
          return;
        }
      }

      const newTab = {
        id: uuid(),
        ...tab,
      };

      setTabs((prevTabs) => {
        return [...prevTabs, { ...newTab }];
      });

      setCurrentTab(newTab.id);
      navigate({ ...getTabLink(newTab), ...navigateOptions });
    },
    [navigate, currentTab, setCurrentTab, tabs, setTabs],
  );
};

export function useCreateNewEmptyTab() {
  const { setCurrentTab } = useCurrentTabAtom();
  const [, setTabs] = useModelTabs();
  const navigate = useNavigateWithSlug();

  return useCallback(() => {
    const newTab: Tab = {
      id: uuid(),
      type: "empty",
    };
    setTabs((prev) => [...prev, { ...newTab }]);
    setCurrentTab(newTab.id);
    navigate(getTabLink(newTab));
    return newTab.id;
  }, [navigate, setTabs, setCurrentTab]);
}

export function useCloseCurrentTab() {
  const { currentTab, setCurrentTab } = useCurrentTabAtom();
  const [tabs, setTabs] = useModelTabs();
  const navigate = useNavigateWithSlug();

  return useCallback(() => {
    if (!currentTab) return;
    const currentTabIndex = tabs.findIndex((tab) => tab.id === currentTab.id);
    if (currentTabIndex === -1) return;
    const filteredTabs = tabs.filter((tab) => tab.id !== currentTab.id);
    setTabs(filteredTabs);
    if (filteredTabs.length > 0) {
      const newCurrentTabIndex = Math.max(0, currentTabIndex - 1);
      const newCurrentTab = filteredTabs[newCurrentTabIndex];
      setCurrentTab(newCurrentTab.id);
      navigate(getTabLink(newCurrentTab));
    } else {
      setCurrentTab(null);
      navigate(getTabLink(undefined));
    }
  }, [navigate, currentTab, setCurrentTab, setTabs, tabs]);
}

export const useOpenModelInTab = () => {
  const handleOpenTab = useSetCurrentTab();
  return useCallback(
    (
      params: {
        modelId: string;
        type?: ModelType;
        dashboardId?: string;
        tileId?: string;
      },
      navigateOptions?: Pick<
        NavigateOptions<{}>,
        "search" | "replace" | "hash"
      >,
    ) => {
      handleOpenTab({ type: "model", ...params }, navigateOptions);
    },
    [handleOpenTab],
  );
};

export const useOpenDraftInTab = () => {
  const handleOpenTab = useSetCurrentTab();
  return useCallback(
    (draftId: string, draftNr: number, openNew?: true) => {
      handleOpenTab({
        modelId: draftId,
        type: "draft",
        label: "Unsaved draft" + (draftNr > 1 ? ` (${draftNr})` : ""),
      });
    },
    [handleOpenTab],
  );
};

export const useOpenDashboardInTab = () => {
  const handleOpenTab = useSetCurrentTab();
  return useCallback(
    (dashboardId: string, dashboardName: string, openNew?: true) => {
      handleOpenTab({
        dashboardId,
        type: "dashboard",
        label: dashboardName,
      });
    },
    [handleOpenTab],
  );
};

export const useStoreLatestPreviewForTab = () => {
  const [, setTabs] = useModelTabs();
  const currentTab = useCurrentTab();

  return useCallback(
    (previewId: string) => {
      setTabs((prevTabs) => {
        return prevTabs.map((tab) =>
          tab.id === currentTab?.id
            ? { ...tab, latestPreviewId: previewId }
            : tab,
        );
      });
    },
    [currentTab?.id, setTabs],
  );
};

export const useCloseRemovedTab = () => {
  const { currentTab, setCurrentTab } = useCurrentTabAtom();

  const [, setTabs] = useModelTabs();

  const navigate = useNavigateWithSlug();

  const closeRemovedModelTab = useCallback(
    (type: "model" | "dashboard", id: string) => {
      let newCurrentTab: Tab | undefined;

      setTabs((p) => {
        const filteredTabs = p.filter((tab) => {
          if (type === "dashboard") return tab.dashboardId !== id;
          return tab.modelId !== id;
        });
        newCurrentTab = filteredTabs[0];

        return filteredTabs;
      });

      if (currentTab?.modelId === id || currentTab?.dashboardId === id) {
        setCurrentTab(newCurrentTab?.id ?? null);
        navigate(getTabLink(newCurrentTab));
      }
    },
    [
      currentTab?.dashboardId,
      currentTab?.modelId,
      navigate,
      setCurrentTab,
      setTabs,
    ],
  );

  return closeRemovedModelTab;
};

export const useCurrentTabLink = () => {
  const currentTab = useCurrentTab();
  const routerInput = useTabLink(currentTab ?? undefined);

  return useMemo(() => {
    return (
      routerInput.to +
      (typeof routerInput.search === "string" ? "?" + routerInput.search : "")
    );
  }, [routerInput]);
};

export const useTabLink = (tab?: Tab) => {
  return getTabLink(tab);
};

export const getTabLink = (
  tab?: Tab,
): Parameters<ReturnType<typeof useNavigateWithSlug>>[0] => {
  if (tab?.modelId && tab.type === "model") {
    return {
      to: `/editor/${tab.modelId}`,
      search: (prev) => ({
        ...prev,
        dashboardId: tab.dashboardId,
        tileId: tab.tileId,
      }),
    };
  }
  if (tab?.modelId && tab.type === "draft") {
    return { to: `/editor/draft/${tab.modelId}`, search: true };
  }
  if (tab?.dashboardId && tab.type === "dashboard") {
    return { to: `/editor/dashboard/${tab.dashboardId}`, search: true };
  }

  return { to: `/editor`, search: true };
};
